import type { SchemaField, SchemaTab } from '@cli-types/core-engine/schema.js'
import type {
  EntityFormLayout,
  EntityFormRenderedSidebarFieldEntry,
  EntityFormSidebarFieldEntry,
  TopLevelTabComponentSpec
} from '@cli-types/next-adapter/forms.js'
import { toKebabCase, toPascalCase } from '@cli-utils/text.js'
import { generateFieldJSX } from '@next-adapter/generators/form/field-jsx/generate-field-jsx.js'
import { collectTabIconNames } from '@next-adapter/generators/form/form-shared/collect-tab-icon-names.js'
import { renderTabsTrigger } from '@next-adapter/generators/form/form-shared/render-tabs-trigger.js'
import { renderEntityFormContentRenderer } from '@next-adapter/templates/renderers/generators/form/form-entity/generate-form-content.js'

export function buildEntityFormLayout({
  allFormFields,
  schemaName,
  Singular,
  hasDraft,
  showMetadataWidget,
  creatableSelectFieldNames,
  defaultValueFromSourceNames,
  tabFieldNames
}: {
  allFormFields: SchemaField[]
  schemaName: string
  Singular: string
  hasDraft: boolean
  showMetadataWidget: boolean
  creatableSelectFieldNames: Set<string>
  defaultValueFromSourceNames: Set<string>
  tabFieldNames: Set<string>
}): EntityFormLayout {
  const cardFieldIndent = '                '

  const hasRenderableTabsField = (fields: SchemaField[], excludedField?: SchemaField): boolean => {
    for (const field of fields) {
      if (field.hidden) continue
      if (field.type === 'tabs' && field !== excludedField) return true
      if ((field.type === 'group' || field.type === 'section') && field.fields) {
        if (hasRenderableTabsField(field.fields, excludedField)) return true
      }
      if (field.type === 'tabs' && field.tabs) {
        for (const tab of field.tabs) {
          if (hasRenderableTabsField(tab.fields ?? [], excludedField)) return true
        }
      }
    }

    return false
  }

  const renderNestedTabsField = (
    field: SchemaField,
    childContext: { slotFilter?: 'main' | 'sidebar' } = {}
  ): string => {
    if (!field.tabs) return ''

    const tabsList = field.tabs
      .map((tab) => renderTabsTrigger(tab, `${cardFieldIndent}    `))
      .join('\n')
    const tabsContent = field.tabs
      .map((tab) => {
        const tabFields = (tab.fields || [])
          .map((tabField) =>
            generateFieldJSX(tabField, `${cardFieldIndent}    `, {
              creatableSelectFieldNames,
              defaultValueFromSourceNames,
              schemaName,
              ...childContext
            })
          )
          .filter(Boolean)
          .join('\n')

        return renderEntityFormContentRenderer({
          kind: 'renderFormTabContentTemplate',
          props: {
            indent: cardFieldIndent,
            tName: tab.name,
            tabFields
          }
        })
      })
      .join('\n')

    return renderEntityFormContentRenderer({
      kind: 'renderFormTabsLayoutTemplate',
      props: {
        indent: cardFieldIndent,
        tabsList,
        tabsContent
      }
    })
  }

  const collectSidebarFields = (
    fields: SchemaField[],
    tabName?: string
  ): EntityFormSidebarFieldEntry[] => {
    const sidebarFields: EntityFormSidebarFieldEntry[] = []

    for (const field of fields) {
      if (field.slot === 'sidebar') {
        sidebarFields.push({ field, tabName })
        continue
      }

      if ((field.type === 'group' || field.type === 'section') && field.fields) {
        sidebarFields.push(...collectSidebarFields(field.fields, tabName))
      }
      if (field.type === 'tabs' && field.tabs) {
        for (const tab of field.tabs) {
          sidebarFields.push(...collectSidebarFields(tab.fields || [], tab.name))
        }
      }
    }

    return sidebarFields
  }

  const renderMainField = (field: SchemaField): string => {
    if ((field.slot ?? 'main') !== 'main') return ''
    if (field.type === 'tabs') return renderNestedTabsField(field, { slotFilter: 'main' })
    if (tabFieldNames.has(field.name)) return ''
    return generateFieldJSX(field, cardFieldIndent, {
      creatableSelectFieldNames,
      defaultValueFromSourceNames,
      schemaName,
      slotFilter: 'main'
    })
  }

  const renderPromotedSidebarField = (field: SchemaField): string => {
    if (field.type === 'tabs') return renderNestedTabsField(field)
    return generateFieldJSX(field, cardFieldIndent, {
      creatableSelectFieldNames,
      defaultValueFromSourceNames,
      schemaName
    })
  }

  const renderConditionalSidebarEntry = ({
    field,
    tabName
  }: EntityFormSidebarFieldEntry): string => {
    const fieldJSX = renderPromotedSidebarField(field)
    if (!fieldJSX) return ''
    if (!tabName) return fieldJSX
    if (!field.hasIcon) {
      return renderEntityFormContentRenderer({
        kind: 'renderFormContentSerializedValueTemplate',
        props: {
          cardFieldIndent,
          stringify: JSON.stringify(tabName),
          fieldJsx: fieldJSX
        }
      })
    }

    return renderEntityFormContentRenderer({
      kind: 'renderFormContentSerializedValueBranchTemplate',
      props: {
        cardFieldIndent,
        stringify: JSON.stringify(tabName),
        fieldJsx: fieldJSX
      }
    })
  }

  const renderStaticSidebarEntry = ({ field }: EntityFormSidebarFieldEntry): string =>
    renderPromotedSidebarField(field)

  const renderSidebarCardHeading = (field: SchemaField, indent: string): string => {
    const heading = field.heading
    if (!heading?.title) return ''

    const iconName = heading.icon?.trim() ? toPascalCase(heading.icon.trim()) : null
    const iconJSX = iconName
      ? renderEntityFormContentRenderer({
          kind: 'renderFormLayoutTemplate',
          props: { indent, iconName }
        })
      : ''

    return renderEntityFormContentRenderer({
      kind: 'renderFormCardHeaderTemplate',
      props: {
        indent,
        iconJsx: iconJSX,
        stringify: JSON.stringify(heading.title)
      }
    })
  }

  const renderSidebarCard = (
    fieldsJSX: string,
    indent = '              ',
    headingField?: SchemaField
  ): string => {
    if (!fieldsJSX) return ''
    const headingJSX = headingField ? renderSidebarCardHeading(headingField, indent) : ''
    if (headingJSX) {
      return renderEntityFormContentRenderer({
        kind: 'renderFormCardContentTemplate',
        props: {
          indent,
          headingJsx: headingJSX,
          fieldsJsx: fieldsJSX
        }
      })
    }
    return renderEntityFormContentRenderer({
      kind: 'renderFormCardLayoutTemplate',
      props: { indent, fieldsJsx: fieldsJSX }
    })
  }

  const wrapSidebarCardForTab = (cardJSX: string, tabName?: string): string => {
    if (!tabName) return cardJSX

    return renderEntityFormContentRenderer({
      kind: 'renderFormContentSerializedValueBranchBodyTemplate',
      props: {
        stringify: JSON.stringify(tabName),
        cardJsx: cardJSX
      }
    })
  }

  const renderSidebarColumn = (
    entries: EntityFormRenderedSidebarFieldEntry[],
    options: { conditionalTabs?: boolean; scrollable?: boolean } = {}
  ): string => {
    if (entries.length === 0) return ''

    const cards: string[] = []
    let defaultCardFields: string[] = []

    const flushDefaultCardFields = (): void => {
      const fieldsJSX = defaultCardFields.filter(Boolean).join('\n')
      if (!fieldsJSX) return
      cards.push(renderSidebarCard(fieldsJSX, '                '))
      defaultCardFields = []
    }

    for (const entry of entries) {
      if (!(entry.field.type === 'section' && entry.field.slot === 'sidebar')) {
        defaultCardFields.push(entry.jsx)
        continue
      }

      flushDefaultCardFields()
      const cardJSX = renderSidebarCard(entry.jsx, '                ', entry.field)
      cards.push(options.conditionalTabs ? wrapSidebarCardForTab(cardJSX, entry.tabName) : cardJSX)
    }

    flushDefaultCardFields()

    return renderEntityFormContentRenderer({
      kind: 'renderFormContentClassNameTemplate',
      props: {
        optionsScrollableClassName: options.scrollable
          ? 'className="relative h-fit max-h-full space-y-4 overflow-y-auto pb-4 sticky top-0"'
          : showMetadataWidget
            ? renderEntityFormContentRenderer({ kind: 'renderFormLayoutDetailsTemplate' })
            : 'className="sticky top-14 space-y-4 pb-4"',
        join: cards.join('\n')
      }
    })
  }

  const renderTabMainFields = (tab: SchemaTab, commonMainFieldsJSX: string): string => {
    const tabFieldsJSX = (tab.fields || [])
      .map((field) =>
        generateFieldJSX(field, cardFieldIndent, {
          creatableSelectFieldNames,
          defaultValueFromSourceNames,
          schemaName,
          slotFilter: 'main'
        })
      )
      .filter(Boolean)
      .join('\n')

    return [commonMainFieldsJSX, tabFieldsJSX].filter(Boolean).join('\n')
  }

  const renderTopLevelTabFormBody = (
    tab: SchemaTab,
    commonMainFieldsJSX: string,
    commonSidebarEntries: EntityFormSidebarFieldEntry[]
  ): string => {
    const tabMainFieldsJSX = renderTabMainFields(tab, commonMainFieldsJSX)
    const tabSidebarEntries = [
      ...commonSidebarEntries,
      ...collectSidebarFields(tab.fields || [], tab.name)
    ]
      .map((entry) => ({ ...entry, jsx: renderStaticSidebarEntry(entry) }))
      .filter((entry): entry is EntityFormRenderedSidebarFieldEntry => entry.jsx.length > 0)
    const sidebarColumn = renderSidebarColumn(tabSidebarEntries)

    return renderEntityFormContentRenderer({
      kind: 'renderFormCardContentGridTemplate',
      props: {
        tabSidebarEntriesGrid:
          tabSidebarEntries.length > 0 ? 'grid-cols-[1fr_360px]' : 'grid-cols-1',
        tabMainFieldsJsx: tabMainFieldsJSX,
        sidebarColumn
      }
    })
  }

  const topLevelTabComponentName = (tab: SchemaTab): string =>
    `${Singular}FormTab${toPascalCase(tab.name)}`
  const topLevelTabFileBaseName = (tab: SchemaTab): string =>
    `${schemaName}-form-tab-${toKebabCase(tab.name)}`

  const renderTopLevelTabComponentSpecs = (
    tabsField: SchemaField,
    commonFields: SchemaField[]
  ): TopLevelTabComponentSpec[] => {
    if (!tabsField.tabs) return []

    const commonMainFieldsJSX = commonFields.map(renderMainField).filter(Boolean).join('\n')
    const commonSidebarEntries = collectSidebarFields(commonFields)
    return tabsField.tabs.map((tab) => ({
      tab,
      componentName: topLevelTabComponentName(tab),
      fileBaseName: topLevelTabFileBaseName(tab),
      body: renderTopLevelTabFormBody(tab, commonMainFieldsJSX, commonSidebarEntries)
    }))
  }

  const renderTopLevelTabComponentSwitch = (spec: TopLevelTabComponentSpec): string =>
    `      {tab === ${JSON.stringify(spec.tab.name)} ? <${spec.componentName} state={state} /> : null}`

  const visibleFields = allFormFields.filter((field) => !(hasDraft && field.name === 'published'))
  const topLevelTabsField = visibleFields.find(
    (field) => field.type === 'tabs' && (field.slot ?? 'main') === 'main' && field.tabs?.length
  )
  const topLevelCommonFields = topLevelTabsField
    ? visibleFields.filter((field) => field !== topLevelTabsField)
    : visibleFields
  const topLevelTabComponents = topLevelTabsField
    ? renderTopLevelTabComponentSpecs(topLevelTabsField, topLevelCommonFields)
    : []
  const contentUsesTabsField = topLevelTabsField
    ? hasRenderableTabsField(topLevelCommonFields, topLevelTabsField) ||
      topLevelTabsField.tabs?.some((tab) =>
        hasRenderableTabsField(tab.fields ?? [], topLevelTabsField)
      ) === true
    : allFormFields.some((field) => field.type === 'tabs')
  const topLevelTabChildFields =
    topLevelTabsField?.tabs?.flatMap((tab) => [
      ...(tab.fields ?? []),
      ...(tab.slot?.main?.fields ?? []),
      ...(tab.slot?.sidebar?.fields ?? [])
    ]) ?? []
  const contentTabIconNames = collectTabIconNames(
    topLevelTabsField ? [...topLevelCommonFields, ...topLevelTabChildFields] : allFormFields
  )
  const topLevelTabIconNames = Array.from(
    new Set(
      topLevelTabsField?.tabs?.flatMap((tab) => {
        const icon = tab.icon?.trim()
        return icon ? [toPascalCase(icon)] : []
      }) ?? []
    )
  )
  const mainFieldsJSX = topLevelCommonFields.map(renderMainField).filter(Boolean).join('\n')
  const sidebarEntries = collectSidebarFields(topLevelCommonFields)
    .map((entry) => ({ ...entry, jsx: renderConditionalSidebarEntry(entry) }))
    .filter((entry): entry is EntityFormRenderedSidebarFieldEntry => entry.jsx.length > 0)
  const hasSidebarFields = sidebarEntries.length > 0
  const hasCommonSidebarFields = sidebarEntries.some((entry) => !entry.tabName)
  const sidebarTabNames = Array.from(
    new Set(sidebarEntries.flatMap((entry) => (entry.tabName ? [entry.tabName] : [])))
  )
  const shouldGuardSidebarByTab =
    hasSidebarFields && !hasCommonSidebarFields && sidebarTabNames.length > 0
  const sidebarVisibilityState = shouldGuardSidebarByTab
    ? `\n  const hasVisibleSidebarFields = ${sidebarTabNames.map((tabName) => `activeTab === ${JSON.stringify(tabName)}`).join(' || ')}\n`
    : ''
  const formGridOpen = shouldGuardSidebarByTab
    ? renderEntityFormContentRenderer({ kind: 'renderFormLayoutDetailsBranchTemplate' })
    : `            <div className="grid ${hasSidebarFields ? 'grid-cols-[1fr_360px]' : 'grid-cols-1'} gap-4 items-start h-full">`
  const sidebarCardJSX = !hasSidebarFields
    ? ''
    : shouldGuardSidebarByTab
      ? renderEntityFormContentRenderer({
          kind: 'renderFormContentSidebarColumnTemplate',
          props: {
            renderSidebarColumn: renderSidebarColumn(sidebarEntries, { conditionalTabs: true })
          }
        })
      : renderSidebarColumn(sidebarEntries, { conditionalTabs: true })
  const formBodyJSX = topLevelTabsField
    ? topLevelTabComponents.map(renderTopLevelTabComponentSwitch).join('\n')
    : renderEntityFormContentRenderer({
        kind: 'renderFormCardContentDetailsTemplate',
        props: {
          formGridOpen,
          mainFieldsJsx: mainFieldsJSX,
          sidebarCardJsx: sidebarCardJSX
        }
      })

  return {
    topLevelTabsField,
    topLevelTabComponents,
    contentUsesTabsField,
    contentTabIconNames,
    topLevelTabIconNames,
    formBodyJSX,
    hasSidebarFields,
    shouldGuardSidebarByTab,
    sidebarVisibilityState
  }
}
